import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { 
  CreditCard, 
  Calendar, 
  DollarSign, 
  Download, 
  Settings, 
  CheckCircle,
  Clock,
  AlertCircle
} from 'lucide-react';
import useAuthStore from '../stores/authStore';

const Billing = () => {
  const { user } = useAuthStore();

  const mockInvoices = [
    {
      id: 'INV-2024-001',
      date: '2024-09-01',
      amount: '$99.00',
      status: 'paid',
      description: 'Professional Plan - September 2024'
    },
    {
      id: 'INV-2024-002',
      date: '2024-08-01',
      amount: '$99.00',
      status: 'paid',
      description: 'Professional Plan - August 2024'
    },
    {
      id: 'INV-2024-003',
      date: '2024-07-01',
      amount: '$99.00',
      status: 'paid',
      description: 'Professional Plan - July 2024'
    }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'overdue':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      paid: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      overdue: 'bg-red-100 text-red-800'
    };

    return (
      <Badge className={variants[status] || 'bg-gray-100 text-gray-800'}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Billing & Subscription</h1>
        <p className="text-gray-600 mt-2">
          Manage your subscription, payment methods, and billing history.
        </p>
      </div>

      {/* Current Plan */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <CreditCard className="h-5 w-5" />
            <span>Current Plan</span>
          </CardTitle>
          <CardDescription>
            Your active subscription and usage details.
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <div className="flex items-center justify-between p-6 bg-blue-50 rounded-lg border border-blue-200">
            <div>
              <h3 className="text-lg font-semibold text-blue-900">Professional Plan</h3>
              <p className="text-blue-700 mt-1">Full access to all features and analytics</p>
              <div className="flex items-center space-x-4 mt-3 text-sm text-blue-600">
                <span className="flex items-center space-x-1">
                  <Calendar className="h-4 w-4" />
                  <span>Renews Oct 1, 2024</span>
                </span>
                <span className="flex items-center space-x-1">
                  <DollarSign className="h-4 w-4" />
                  <span>$99/month</span>
                </span>
              </div>
            </div>
            <div className="text-right">
              <Badge className="bg-green-100 text-green-800 mb-2">Active</Badge>
              <div className="space-y-2">
                <Button variant="outline" size="sm">
                  <Settings className="h-4 w-4 mr-2" />
                  Manage Plan
                </Button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">2,450</div>
              <div className="text-sm text-gray-600">API Calls This Month</div>
              <div className="text-xs text-gray-500 mt-1">of 10,000 limit</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">15</div>
              <div className="text-sm text-gray-600">Hotels Monitored</div>
              <div className="text-xs text-gray-500 mt-1">of 50 limit</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">24/7</div>
              <div className="text-sm text-gray-600">Support Access</div>
              <div className="text-xs text-gray-500 mt-1">Premium support</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment Method */}
      <Card>
        <CardHeader>
          <CardTitle>Payment Method</CardTitle>
          <CardDescription>
            Manage your payment methods and billing information.
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-6 bg-blue-600 rounded flex items-center justify-center">
                <span className="text-white text-xs font-bold">VISA</span>
              </div>
              <div>
                <div className="font-medium">•••• •••• •••• 4242</div>
                <div className="text-sm text-gray-500">Expires 12/2027</div>
              </div>
            </div>
            <Button variant="outline" size="sm">
              Update
            </Button>
          </div>
          
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center space-x-2 text-yellow-800">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm font-medium">Payment Method Setup Required</span>
            </div>
            <p className="text-sm text-yellow-700 mt-1">
              This is a placeholder billing system. Payment integration will be implemented in the next phase.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Billing History */}
      <Card>
        <CardHeader>
          <CardTitle>Billing History</CardTitle>
          <CardDescription>
            View and download your past invoices and payments.
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <div className="space-y-4">
            {mockInvoices.map((invoice, index) => (
              <div key={invoice.id}>
                <div className="flex items-center justify-between py-3">
                  <div className="flex items-center space-x-3">
                    {getStatusIcon(invoice.status)}
                    <div>
                      <div className="font-medium">{invoice.id}</div>
                      <div className="text-sm text-gray-500">{invoice.description}</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="font-medium">{invoice.amount}</div>
                      <div className="text-sm text-gray-500">
                        {new Date(invoice.date).toLocaleDateString()}
                      </div>
                    </div>
                    {getStatusBadge(invoice.status)}
                    <Button variant="ghost" size="sm">
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {index < mockInvoices.length - 1 && <Separator />}
              </div>
            ))}
          </div>

          <div className="mt-6 text-center">
            <Button variant="outline">
              View All Invoices
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Billing Contact */}
      <Card>
        <CardHeader>
          <CardTitle>Need Help?</CardTitle>
          <CardDescription>
            Contact our billing support team for assistance.
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border rounded-lg">
              <h4 className="font-medium mb-2">Billing Questions</h4>
              <p className="text-sm text-gray-600 mb-3">
                Have questions about your bill or need to update your payment method?
              </p>
              <Button variant="outline" size="sm">
                Contact Billing Support
              </Button>
            </div>
            <div className="p-4 border rounded-lg">
              <h4 className="font-medium mb-2">Plan Changes</h4>
              <p className="text-sm text-gray-600 mb-3">
                Want to upgrade, downgrade, or cancel your subscription?
              </p>
              <Button variant="outline" size="sm">
                Manage Subscription
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Billing;
